/**
 * User-state persistence + subscription helper.
 * ─────────────────────────────────────────────────────────────
 * BuildPilot is live in South Australia only. We infer a user's state
 * from their pick in the header state-chip and persist it so downstream
 * surfaces (H&L, Home Designs, land) can render an "this is SA-only
 * content" banner for anyone who's told us they're not in SA.
 *
 * Contract:
 *   - `getUserState()`  → 'SA' | 'VIC' | 'NSW' | ... | 'SA' (default)
 *   - `setUserState(x)` → persists + broadcasts a `bp:state-changed`
 *     CustomEvent so any live component can react in the same tab.
 *   - `useUserState()`  → React hook, re-renders on change (same tab
 *     via CustomEvent, cross-tab via native `storage` event).
 *
 * Why a custom event? `localStorage` writes DO NOT trigger the native
 * `storage` event in the same tab that made the write - only in other
 * tabs. So we dispatch our own to keep the current tab in sync.
 */
import { useEffect, useState } from 'react';

export const STATE_STORAGE_KEY = 'bp:userState';
export const STATE_SOURCE_KEY = 'bp:userStateSource'; // 'manual' | 'geo'
export const STATE_CHANGE_EVENT = 'bp:state-changed';
export const LOCATION_PROMPT_KEY = 'bp:locationPrompted'; // '1' once the first-visit location modal has been answered/dismissed

/** Has the first-visit "Where are you building?" modal already been answered or dismissed? */
export function hasBeenLocationPrompted() {
  if (typeof window === 'undefined') return true;
  try { return window.localStorage.getItem(LOCATION_PROMPT_KEY) === '1'; } catch { return false; }
}

/** Mark the location modal as answered/dismissed so it never re-shows for this browser. */
export function markLocationPrompted() {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(LOCATION_PROMPT_KEY, '1'); } catch { /* storage disabled */ }
}

const KNOWN_STATES = new Set(['SA', 'VIC', 'NSW', 'QLD', 'WA', 'TAS', 'NT', 'ACT']);

export const STATE_NAMES = {
  SA: 'South Australia',
  VIC: 'Victoria',
  NSW: 'New South Wales',
  QLD: 'Queensland',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'ACT',
};

// States where BuildPilot has real live inventory (live builder
// matching, house-and-land feed, editorial + council coverage). Every
// other state is treated as a waitlist state - the home page hides
// SA/VIC-specific sections and surfaces a "Coming soon to X" callout
// with the waitlist CTA so it never looks like SA content stranded
// behind a NSW/QLD/WA/TAS/NT/ACT chip.
export const LIVE_STATES = new Set(['SA', 'VIC']);
export const isLiveState = (code) => LIVE_STATES.has(code);

/**
 * State adjectives + capitals. Used by the home page (and any future
 * generic surface) to swap the descriptor of "builders / designs /
 * products" so the copy reads native to the visitor's picked state.
 * Only populated for LIVE states - non-live states fall back to a
 * neutral copy path.
 */
export const STATE_ADJECTIVES = {
  SA: 'Adelaide',
  VIC: 'Melbourne',
};

export const STATE_CAPITALS = {
  SA: 'Adelaide',
  VIC: 'Melbourne',
};

export function getUserState() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STATE_STORAGE_KEY);
    if (raw && KNOWN_STATES.has(raw)) return raw;
  } catch { /* localStorage unavailable / disabled - fall through */ }
  return null;
}

/**
 * Like getUserState() but returns null when we DON'T actually know the
 * state (nothing stored). Use this for gating decisions - e.g. skipping
 * the matcher's "which state" question - where the SA default must NOT
 * be assumed. getUserState() (SA default) stays for content surfaces.
 */
export function getStoredUserState() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STATE_STORAGE_KEY);
    if (raw && KNOWN_STATES.has(raw)) return raw;
  } catch { /* localStorage unavailable */ }
  return null;
}

export function setUserState(next, source = 'manual') {
  if (typeof window === 'undefined') return;
  const code = typeof next === 'string' ? next.toUpperCase() : '';
  if (!KNOWN_STATES.has(code)) return;
  try {
    window.localStorage.setItem(STATE_STORAGE_KEY, code);
    window.localStorage.setItem(STATE_SOURCE_KEY, source === 'geo' ? 'geo' : 'manual');
  } catch { /* quota / disabled - still fire the in-tab event so UI can update */ }
  try {
    window.dispatchEvent(new CustomEvent(STATE_CHANGE_EVENT, { detail: { state: code, source } }));
  } catch { /* very old browsers - swallow */ }
}

/**
 * Has the user (or a prior geo-detect) already resolved a state for this
 * browser? Used to decide whether auto-detect should run at all.
 */
export function hasStoredUserState() {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(STATE_STORAGE_KEY);
    return !!(raw && KNOWN_STATES.has(raw));
  } catch { return false; }
}

/**
 * Soft IP-based default. Runs ONCE per browser: only when the visitor has
 * never had a state resolved (no manual pick, no prior geo-set). Respects
 * a manual choice forever. Never blocks UI - silently no-ops on any error.
 * Works identically for logged-in and logged-out users (it's IP + local
 * storage based, independent of auth).
 */
export async function autoDetectUserState() {
  if (typeof window === 'undefined') return;
  if (hasStoredUserState()) return; // already resolved - never override
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/geo/state`;
    const res = await fetch(url, { credentials: 'omit' });
    if (!res.ok) return;
    const data = await res.json();
    if (hasStoredUserState()) return; // a manual pick may have landed while we waited
    const code = (data && data.state ? String(data.state) : '').toUpperCase();
    if (KNOWN_STATES.has(code)) setUserState(code, 'geo');
  } catch { /* network / ad-block - keep the built-in SA default */ }
}

export function useUserState() {
  const [state, setState] = useState(() => getUserState());
  useEffect(() => {
    const onChange = (e) => {
      const next = (e && e.detail && e.detail.state) || getUserState();
      setState(next);
    };
    const onStorage = (e) => {
      if (e.key === STATE_STORAGE_KEY) setState(getUserState());
    };
    window.addEventListener(STATE_CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(STATE_CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return state;
}

/**
 * SA-only gating. Unlike getUserState()/useUserState() (which default to
 * SA for CONTENT surfaces), this returns true ONLY when the visitor's
 * state has been positively resolved to SA - via a manual switcher pick
 * OR an IP geo lookup. Unknown (nothing stored, geo failed, bot) and any
 * confirmed non-SA state return false, so SA-only features (phone number,
 * direct call, One on One Support) fail closed for everyone we can't
 * confirm is in South Australia.
 */
export function isSAConfirmed() {
  return getStoredUserState() === 'SA';
}

export function useIsSA() {
  const [sa, setSa] = useState(() => isSAConfirmed());
  useEffect(() => {
    const update = () => setSa(isSAConfirmed());
    window.addEventListener(STATE_CHANGE_EVENT, update);
    window.addEventListener('storage', update);
    update(); // re-check in case autoDetect resolved before we subscribed
    return () => {
      window.removeEventListener(STATE_CHANGE_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return sa;
}
